// frontend/src/pages/PaymentReceiptsPage.js
//
// Reçus de paiement — proof of money actually received, for the workshop and
// supplier dashboards. Most clients here pay cash and ask for something on
// paper; an invoice marked "réglée" lives inside the app and says nothing
// about when or how the money arrived.
//
// One page serves both roles, because the document is identical and only the
// lookups differ. A workshop can be offered its own cars (/vehicles/all is
// scoped to vehicles assigned to it), so choosing a plate fills in the owner.
// A supplier has no vehicle access at all — deliberately, they must never be
// handed a customer list — so for them every field is typed. The document is
// free-standing either way: what is printed is the text on the receipt.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import API_URL from '../api/baseURL';
import { generateReceiptPDF, METHOD_LABELS } from '../utils/generateReceiptPDF';
import {
  Page, Panel, Badge, Table, Tr, Td, Button, Field, Modal, Checkbox,
  EmptyState, Loading, ErrorNote, Note, inputClass, selectClass, textareaClass,
} from '../components/ui/Iparc';
import { fmt } from '../theme/iparc';

const todayISO = () => new Date().toISOString().slice(0, 10);

const EMPTY = {
  clientName: '', clientPhone: '', plateNumber: '', vehicleLabel: '', vehicle: '',
  amountPaid: '', amountDue: '', method: 'cash', reference: '',
  paidAt: todayISO(), note: '', sendSms: true,
};

const fmtDate = (d) => {
  if (!d) return '—';
  const t = new Date(d);
  return Number.isNaN(t.getTime())
    ? '—'
    : t.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
};

export default function PaymentReceiptsPage({ token: propToken }) {
  const { token: ctxToken, user } = useAuth();
  const token = propToken || ctxToken;
  const isSupplier = /supplier/i.test(user?.role || '');

  const [receipts, setReceipts] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [issuer, setIssuer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [okMsg, setOkMsg] = useState('');
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [cancelling, setCancelling] = useState(null); // receipt pending cancellation
  const [cancelReason, setCancelReason] = useState('');
  const [cancelSms, setCancelSms] = useState(true);

  const auth = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadReceipts = useCallback(async () => {
    const res = await fetch(`${API_URL}/receipts`, { headers: auth });
    if (!res.ok) throw new Error('Chargement des reçus impossible');
    return res.json();
  }, [auth]);

  useEffect(() => {
    if (!token) { setError('Session expirée — reconnectez-vous.'); setLoading(false); return; }
    let alive = true;
    (async () => {
      try {
        const list = await loadReceipts();
        if (alive) setReceipts(Array.isArray(list) ? list : []);
      } catch (e) {
        if (alive) setError(e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token, loadReceipts]);

  // Workshop-only lookups. A supplier gets a 403 from both, so they are not
  // even attempted — an expected 403 in the console reads like a bug.
  useEffect(() => {
    if (!token || isSupplier || !user?._id) return;
    fetch(`${API_URL}/vehicles/all`, { headers: auth })
      .then((r) => (r.ok ? r.json() : []))
      .then((v) => setVehicles(Array.isArray(v) ? v : []))
      .catch(() => { });
    fetch(`${API_URL}/workshops/${user._id}`, { headers: auth })
      .then((r) => (r.ok ? r.json() : null))
      .then((w) => w && setIssuer(w))
      .catch(() => { });
  }, [token, isSupplier, user, auth]);

  // What the PDF header prints. The workshop record is richer (logo, tax id);
  // the signed-in user is the fallback, and all a supplier has.
  const issuerData = useMemo(() => ({
    businessName: issuer?.businessName || user?.businessName || user?.name,
    name: user?.name,
    address: issuer?.address || user?.address,
    phone: issuer?.phone || user?.contactNumber || user?.phoneNumber,
    email: issuer?.email || user?.email,
    taxId: issuer?.taxId,
    logo: issuer?.logo,
  }), [issuer, user]);

  const byPlate = useMemo(() => {
    const m = new Map();
    for (const v of vehicles) {
      if (v?.plateNumber) m.set(String(v.plateNumber).toUpperCase().trim(), v);
    }
    return m;
  }, [vehicles]);

  const set = (k) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => {
      const next = { ...f, [k]: value };
      // Choosing a known plate fills in the rest — the whole reason a workshop
      // gets a dropdown here. Every field stays editable afterwards.
      if (k === 'plateNumber') {
        const veh = byPlate.get(String(value).toUpperCase().trim());
        if (veh) {
          next.vehicle = veh._id;
          next.vehicleLabel = [veh.brand, veh.model].filter(Boolean).join(' ');
          if (!f.clientName) next.clientName = veh.owner?.name || '';
        } else {
          next.vehicle = '';
        }
      }
      return next;
    });
  };

  const amountPaid = Number(form.amountPaid) || 0;
  const amountDue = Number(form.amountDue) || 0;
  const balance = amountDue > 0 ? Math.max(0, amountDue - amountPaid) : 0;
  const overpaid = amountDue > 0 && amountPaid > amountDue;
  const canSave = form.clientName.trim() && amountPaid > 0 && !overpaid && !saving;

  const resetForm = () => setForm({ ...EMPTY, paidAt: todayISO() });

  const submit = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true);
    setError('');
    setOkMsg('');
    try {
      const res = await fetch(`${API_URL}/receipts`, {
        method: 'POST',
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          amountPaid,
          amountDue,
          // Midday rather than midnight: a bare YYYY-MM-DD is parsed as UTC,
          // and midnight UTC is the previous day west of Greenwich.
          paidAt: `${form.paidAt}T12:00:00`,
          vehicle: form.vehicle || undefined,
          // Explicit rather than relying on the spread: with no number there
          // is nothing to text, and the server would skip it anyway.
          sendSms: form.sendSms && !!form.clientPhone.trim(),
        }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error || 'Enregistrement impossible');

      setReceipts((prev) => [body, ...prev]);
      setOkMsg(`Reçu ${body.documentNumber} enregistré.`);
      resetForm();
      setShowForm(false);
      // The point of the exercise is handing the client a document, so the PDF
      // comes down immediately rather than waiting to be asked for.
      generateReceiptPDF(body, issuerData);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const confirmCancel = async () => {
    if (!cancelling) return;
    try {
      const res = await fetch(`${API_URL}/receipts/${cancelling._id}/cancel`, {
        method: 'PATCH',
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: cancelReason, sendSms: cancelSms }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error || 'Annulation impossible');
      setReceipts((prev) => prev.map((r) => (r._id === body._id ? body : r)));
      setOkMsg(`Reçu ${body.documentNumber} annulé.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setCancelling(null);
      setCancelReason('');
    }
  };

  const live = receipts.filter((r) => !r.cancelledAt);
  const monthTotal = live
    .filter((r) => {
      const d = new Date(r.paidAt);
      const now = new Date();
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    })
    .reduce((s, r) => s + (Number(r.amountPaid) || 0), 0);

  return (
    <Page
      subtitle={isSupplier ? 'Fournisseur' : 'Atelier'}
      title="Reçus de paiement"
      actions={
        <Button variant="primary" onClick={() => { setShowForm((v) => !v); setOkMsg(''); }}>
          {showForm ? 'Fermer' : 'Nouveau reçu'}
        </Button>
      }
    >
      {error && <ErrorNote>{error}</ErrorNote>}
      {okMsg && <Note tone="ok">{okMsg}</Note>}

      <Panel>
        <p className="text-[10.5px] uppercase tracking-[.16em] text-[#6E665C] mb-1.5">
          Encaissé ce mois
        </p>
        <p className="font-['Space_Grotesk',sans-serif] tabular-nums text-[30px] font-semibold tracking-[-.02em] leading-none text-[#74A98D]">
          {fmt(monthTotal)} <span className="text-[15px] text-[#6E665C]">F</span>
        </p>
        <p className="text-[11.5px] text-[#8F877B] mt-2">
          {live.length === 0
            ? 'Aucun reçu émis pour le moment.'
            : `${live.length} reçu${live.length > 1 ? 'x' : ''} émis au total.`}
        </p>
      </Panel>

      {showForm && (
        <Panel title="Nouveau reçu">
          <form onSubmit={submit} className="flex flex-col gap-3.5">
            <div className="grid gap-3.5 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
              <Field label="Plaque d'immatriculation" hint={isSupplier ? undefined : 'Choisissez une plaque pour remplir le reste'}>
                <input
                  className={inputClass}
                  list={isSupplier ? undefined : 'receipt-plates'}
                  value={form.plateNumber}
                  onChange={set('plateNumber')}
                  placeholder="AB 1234 CD"
                />
                {!isSupplier && (
                  <datalist id="receipt-plates">
                    {vehicles.map((v) => (
                      <option key={v._id} value={v.plateNumber}>
                        {[v.brand, v.model].filter(Boolean).join(' ')}
                      </option>
                    ))}
                  </datalist>
                )}
              </Field>

              <Field label="Véhicule">
                <input
                  className={inputClass}
                  value={form.vehicleLabel}
                  onChange={set('vehicleLabel')}
                  placeholder="Toyota Hilux"
                />
              </Field>

              <Field label="Nom du client" required>
                <input
                  className={inputClass}
                  value={form.clientName}
                  onChange={set('clientName')}
                  placeholder="Nom et prénom"
                  required
                />
              </Field>

              <Field label="Téléphone du client">
                <input
                  className={inputClass}
                  value={form.clientPhone}
                  onChange={set('clientPhone')}
                  placeholder="07 00 00 00 00"
                />
              </Field>

              <Field label="Montant payé (FCFA)" required>
                <input
                  type="number"
                  min="1"
                  step="1"
                  className={inputClass}
                  value={form.amountPaid}
                  onChange={set('amountPaid')}
                  required
                />
              </Field>

              <Field
                label="Montant total dû (FCFA)"
                hint="Facultatif — renseignez-le pour un paiement partiel"
              >
                <input
                  type="number"
                  min="0"
                  step="1"
                  className={inputClass}
                  value={form.amountDue}
                  onChange={set('amountDue')}
                />
              </Field>

              <Field label="Mode de paiement">
                <select className={selectClass} value={form.method} onChange={set('method')}>
                  {Object.entries(METHOD_LABELS).map(([k, label]) => (
                    <option key={k} value={k}>{label}</option>
                  ))}
                </select>
              </Field>

              <Field label="Référence" hint="N° de transaction, n° de chèque…">
                <input className={inputClass} value={form.reference} onChange={set('reference')} />
              </Field>

              <Field label="Date de paiement" required>
                <input
                  type="date"
                  max={todayISO()}
                  className={inputClass}
                  value={form.paidAt}
                  onChange={set('paidAt')}
                  required
                />
              </Field>
            </div>

            <Field label="Note">
              <textarea
                rows={2}
                className={textareaClass}
                value={form.note}
                onChange={set('note')}
                placeholder="Vidange + plaquettes avant"
              />
            </Field>

            {/* The SMS is how a walk-in with no account hears about this at
                all, so it defaults on — but only a typed number can be
                texted, and the client standing at the counter holding the
                paper does not need one. */}
            <Checkbox
              checked={form.sendSms && !!form.clientPhone.trim()}
              disabled={!form.clientPhone.trim()}
              onChange={set('sendSms')}
              label="Envoyer le reçu par SMS au client"
              hint={
                form.clientPhone.trim()
                  ? `Un SMS sera envoyé au ${form.clientPhone.trim()}.`
                  : 'Renseignez un téléphone pour pouvoir envoyer le SMS.'
              }
            />

            {/* The document says one of three things and the operator should
                know which before printing it, not after handing it over. */}
            {overpaid ? (
              <ErrorNote>Le montant payé dépasse le montant dû.</ErrorNote>
            ) : amountDue > 0 && balance > 0 ? (
              <Note tone="warn">
                Paiement partiel — reste à payer {fmt(balance)} FCFA. Le reçu portera la
                mention <b>ACOMPTE</b>.
              </Note>
            ) : amountPaid > 0 ? (
              <Note tone="ok">Le reçu portera le cachet <b>PAYÉ</b> en rouge.</Note>
            ) : null}

            <div className="flex justify-end gap-2.5">
              <Button type="button" onClick={() => { setShowForm(false); resetForm(); }}>
                Annuler
              </Button>
              <Button type="submit" variant="primary" disabled={!canSave}>
                {saving ? 'Enregistrement…' : 'Enregistrer et imprimer'}
              </Button>
            </div>
          </form>
        </Panel>
      )}

      <Panel title="Reçus émis" bodyClass="p-0">
        {loading ? (
          <Loading label="Chargement des reçus…" />
        ) : receipts.length === 0 ? (
          <EmptyState
            title="Aucun reçu émis"
            hint="Enregistrez un paiement pour remettre au client un justificatif daté et numéroté."
            action={<Button variant="primary" onClick={() => setShowForm(true)}>Nouveau reçu</Button>}
          />
        ) : (
          <Table
            minWidth={900}
            head={['N°', 'Date', 'Client', 'Véhicule', 'Mode', 'Montant', 'État', '']}
          >
            {receipts.map((r) => {
              const cancelled = !!r.cancelledAt;
              const partial = !cancelled && Number(r.balance) > 0;
              return (
                <Tr key={r._id} className={cancelled ? 'opacity-55' : ''}>
                  <Td mono>{r.documentNumber}</Td>
                  <Td>{fmtDate(r.paidAt)}</Td>
                  <Td className="text-[#F4F1EA]">
                    {r.clientName}
                    {r.clientPhone && (
                      <span className="block text-[11px] text-[#6E665C] mt-0.5">
                        {r.clientPhone}
                        {/* "Did they get the text?" is what the counter is
                            asked when a client says they heard nothing. */}
                        {r.smsSentAt && <span className="text-[#74A98D] ml-1.5">SMS ✓</span>}
                      </span>
                    )}
                  </Td>
                  <Td>
                    <span className="font-['Space_Grotesk',sans-serif] tracking-[.02em] text-[#F4F1EA]">
                      {r.plateNumber || '—'}
                    </span>
                    {r.vehicleLabel && (
                      <span className="block text-[11px] text-[#6E665C] mt-0.5">{r.vehicleLabel}</span>
                    )}
                  </Td>
                  <Td>{METHOD_LABELS[r.method] || r.method}</Td>
                  <Td mono className="text-right">
                    {fmt(r.amountPaid)} <span className="text-[10.5px] text-[#6E665C]">F</span>
                    {partial && (
                      <span className="block text-[10.5px] text-[#DFA23C] font-normal mt-0.5">
                        reste {fmt(r.balance)} F
                      </span>
                    )}
                  </Td>
                  <Td>
                    <Badge tone={cancelled ? 'neutral' : partial ? 'warn' : 'ok'}>
                      {cancelled ? 'Annulé' : partial ? 'Acompte' : 'Payé'}
                    </Badge>
                  </Td>
                  <Td className="text-right whitespace-nowrap">
                    <Button
                      variant="quiet"
                      className="h-[26px] px-2"
                      onClick={() => generateReceiptPDF(r, issuerData)}
                      title="Télécharger le reçu en PDF"
                    >
                      PDF
                    </Button>
                    {!cancelled && (
                      <Button
                        variant="quiet"
                        className="h-[26px] px-2 text-[#8F877B] hover:text-[#E2574B]"
                        onClick={() => { setCancelling(r); setCancelReason(''); setCancelSms(true); }}
                        title="Annuler ce reçu"
                      >
                        Annuler
                      </Button>
                    )}
                  </Td>
                </Tr>
              );
            })}
          </Table>
        )}
      </Panel>

      <Modal
        open={!!cancelling}
        onClose={() => setCancelling(null)}
        subtitle={cancelling?.documentNumber}
        title="Annuler ce reçu"
        footer={
          <>
            <Button onClick={() => setCancelling(null)}>Retour</Button>
            <Button variant="danger" onClick={confirmCancel}>Confirmer l'annulation</Button>
          </>
        }
      >
        <p className="text-[12.5px] text-[#C9C3B6]">
          Le reçu est conservé et marqué annulé — il n'est jamais supprimé, pour qu'il ne
          manque aucun numéro dans la série. Le client garde sa copie.
        </p>
        <Field label="Motif">
          <input
            className={inputClass}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Erreur de saisie, paiement annulé…"
          />
        </Field>
        {/* Off is the right choice for a same-minute typo you are about to
            reissue — otherwise the client gets received / cancelled /
            received, three texts for one payment. */}
        <Checkbox
          checked={cancelSms && !!cancelling?.clientPhone}
          disabled={!cancelling?.clientPhone}
          onChange={(e) => setCancelSms(e.target.checked)}
          label="Prévenir le client par SMS"
          hint={
            cancelling?.clientPhone
              ? 'Le client détient une copie papier qui ne prouve plus rien.'
              : 'Aucun téléphone sur ce reçu.'
          }
        />
      </Modal>
    </Page>
  );
}
